import {
  eachDayOfInterval as DateFNSEachDayOfInterval,
  formatDistanceToNowStrict as DateFNSFormatDistanceToNowStrict,
  addDays as FNSAddDays,
  addHours as FNSAddHours,
  max as FNSDateMax,
  min as FNSDateMin,
  differenceInCalendarDays as FNSDifferenceInCalendarDays,
  differenceInHours as FNSDifferenceInHours,
  differenceInSeconds as FNSDifferenceInSeconds,
  format,
  parse,
} from 'date-fns';

import { logError } from '@helpers/Loggers';
import { DateRange, Plan } from '@types';

export function getLocalDate() {
  return dateDayFloor(new Date(Date.now()));
}

export function dateDayFloor(date: Date) {
  return new Date(date.setHours(0, 0, 0, 0));
}

export function isDatePast(date: Date) {
  return date < getLocalDate();
}

export function stringToDate(date: string, pattern?: string) {
  const usePattern = pattern || 'MM/dd/yyyy';
  return parse(date, usePattern, new Date());
}

export function dateToString(date: Date, pattern?: string) {
  const usePattern = pattern || 'MM/dd/yyyy';
  return format(date, usePattern);
}

export function eachDayOfInterval({ start, end }: { start: Date; end: Date }) {
  return DateFNSEachDayOfInterval({ start, end });
}

export function dateFormatDistanceToNowStrict(date: Date) {
  return DateFNSFormatDistanceToNowStrict(date);
}

export function dateMax(dates: Date[]) {
  return FNSDateMax(dates);
}

export function dateMin(dates: Date[]) {
  return FNSDateMin(dates);
}

export function addDays(date: Date, amount: number) {
  return FNSAddDays(date, amount);
}

export function addHours(date: Date, amount: number) {
  return FNSAddHours(date, amount);
}

export function differenceInCalendarDays(dateLeft: Date, dateRight: Date) {
  return FNSDifferenceInCalendarDays(dateLeft, dateRight);
}

export function differenceInHours(dateLeft: Date, dateRight: Date) {
  return FNSDifferenceInHours(dateLeft, dateRight);
}

export function differenceInSeconds(dateLeft: Date, dateRight: Date) {
  return FNSDifferenceInSeconds(dateLeft, dateRight);
}

export function dateRangesOverlap(rangeA: DateRange, rangeB: DateRange) {
  if (!rangeA.startDate || !rangeA.endDate || !rangeB.startDate || !rangeB.endDate) return false;

  // for why this date overlap matching logic works,
  // see here: https://stackoverflow.com/a/325964
  return rangeA.startDate <= rangeB.endDate && rangeA.endDate >= rangeB.startDate;
}

export function parsePartialDateRange(range: Partial<DateRange>) {
  try {
    const startDate = range?.startDate ? range.startDate : undefined;
    const endDate = range?.endDate ? range.endDate : undefined;
    if (startDate) {
      const range: DateRange = { startDate, endDate };
      return range;
    }
  } catch (error) {
    logError(error);
  }
  return undefined;
}

export function dateRangeToString(range: DateRange, pattern?: string) {
  try {
    const formatDate = (date: Date) => dateToString(date, pattern);
    if (range?.startDate && range?.endDate) {
      return dateDayFloor(range.startDate).getTime() === dateDayFloor(range.endDate).getTime()
        ? formatDate(range.startDate)
        : formatDate(range.startDate) + ' - ' + formatDate(range.endDate);
    } else if (range?.startDate) {
      return formatDate(range.startDate);
    } else {
      return '';
    }
  } catch (error) {
    logError(error);
  }
  return '';
}

export function filterPastPlans<T extends Pick<Plan, 'endDate'>>(plans: T[]) {
  const today = getLocalDate();
  return plans.filter((plan) => dateDayFloor(plan.endDate) < dateDayFloor(today));
}

export function filterNonPastPlans<T extends Pick<Plan, 'startDate' | 'endDate'>>(plans: T[]) {
  const today = getLocalDate();
  return plans.filter((plan) => dateDayFloor(plan.endDate) >= dateDayFloor(today));
}

export function validateDateRange(dateRange?: DateRange) {
  if (!dateRange) return 'Please select a date.';
  if (!dateRange.startDate || !dateRange.endDate) return 'Please select a date.';
  return '';
}
